/**
 * FilterBar - Barra de filtros hierárquicos
 * Banco > Instituição > Tipo de Operação > Correspondente
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { ChevronRight, Building2, Landmark, FileText, Search, Plus, Edit } from "lucide-react";

export function FilterBar({
  // Dados
  bancos,
  instituicoes,
  tiposOperacao,
  correspondentes,
  instituicoesVinculadas,
  // Seleções
  bancoSelecionado,
  instituicaoSelecionada,
  tipoOperacaoSelecionado,
  correspondenteSelecionado,
  temFiltrosAtivos,
  // Ações de filtro
  onSelecionarBanco,
  onSelecionarInstituicao,
  onSelecionarTipoOperacao,
  onSelecionarCorrespondente,
  onLimparFiltros,
  // Ações de gerenciamento
  onAbrirGerenciarTipos,
  onAbrirGerenciarCorrespondentes,
  onAbrirNovaTabela,
  getNomeInstituicao
}) {
  return (
    <Card>
      <CardHeader className="bg-gradient-to-r from-slate-100 to-slate-50">
        <CardTitle className="text-lg flex items-center gap-2">
          <Search className="h-5 w-5" />
          Navegação Hierárquica
        </CardTitle>
        <CardDescription>Selecione para filtrar as tabelas de comissão</CardDescription>
      </CardHeader>
      <CardContent className="p-6">
        <div className="flex items-center gap-4">
          {/* Seleção de Banco */}
          <div className="flex-1">
            <Label className="text-xs text-slate-500 mb-1 block">1. Banco</Label>
            <Select 
              value={bancoSelecionado?.id || "__all__"} 
              onValueChange={onSelecionarBanco}
            >
              <SelectTrigger className="bg-white">
                <SelectValue placeholder="Selecione o banco" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos os Bancos</SelectItem>
                {bancos.map(banco => (
                  <SelectItem key={banco.id} value={banco.id}>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-emerald-600" />
                      {banco.codigo} - {banco.nome}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <ChevronRight className="h-5 w-5 text-slate-400" />
          
          {/* Seleção de Instituição */}
          <div className="flex-1">
            <Label className="text-xs text-slate-500 mb-1 block">2. Instituição</Label>
            <Select 
              value={instituicaoSelecionada || "__all__"} 
              onValueChange={onSelecionarInstituicao}
              disabled={!bancoSelecionado}
            >
              <SelectTrigger className="bg-white">
                <SelectValue placeholder={bancoSelecionado ? "Selecione a instituição" : "Selecione o banco primeiro"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todas as Instituições</SelectItem>
                {instituicoesVinculadas.length > 0 ? (
                  instituicoesVinculadas.map((inst, idx) => (
                    <SelectItem key={`vinc-${idx}`} value={inst.codigo}>
                      <div className="flex items-center gap-2">
                        <Landmark className="h-4 w-4 text-blue-600" />
                        {inst.nomeCompleto}
                      </div>
                    </SelectItem>
                  ))
                ) : (
                  instituicoes.map((inst, idx) => (
                    <SelectItem key={`inst-${idx}`} value={inst}>
                      <div className="flex items-center gap-2">
                        <Landmark className="h-4 w-4 text-blue-600" />
                        {inst}
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          
          <ChevronRight className="h-5 w-5 text-slate-400" />
          
          {/* Seleção de Tipo de Operação */}
          <div className="flex-1">
            <Label className="text-xs text-slate-500 mb-1 block">3. Tipo de Operação</Label>
            <Select 
              value={tipoOperacaoSelecionado || "__all__"} 
              onValueChange={onSelecionarTipoOperacao}
            >
              <SelectTrigger className="bg-white">
                <SelectValue placeholder="Selecione o tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos os Tipos</SelectItem>
                {tiposOperacao.map((tipo, idx) => (
                  <SelectItem key={`tipo-${idx}`} value={tipo}>
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-orange-600" />
                      {tipo}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <ChevronRight className="h-5 w-5 text-slate-400" />
          
          {/* Seleção de Correspondente */}
          <div className="flex-1">
            <Label className="text-xs text-slate-500 mb-1 block">4. Correspondente</Label>
            <Select 
              value={correspondenteSelecionado || "__all__"} 
              onValueChange={onSelecionarCorrespondente}
            >
              <SelectTrigger className="bg-white">
                <SelectValue placeholder="Selecione o correspondente" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos os Correspondentes</SelectItem>
                {correspondentes.map((corresp, idx) => (
                  <SelectItem key={`corresp-${idx}`} value={corresp}>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-purple-600" />
                      {corresp}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Breadcrumb de seleção */}
        {temFiltrosAtivos && (
          <div className="mt-4 flex items-center gap-2 text-sm flex-wrap">
            <span className="text-slate-500">Filtro ativo:</span>
            {bancoSelecionado && (
              <Badge variant="secondary" className="gap-1">
                <Building2 className="h-3 w-3" />
                {bancoSelecionado.nome}
              </Badge>
            )}
            {instituicaoSelecionada && (
              <>
                <ChevronRight className="h-4 w-4 text-slate-400" />
                <Badge variant="secondary" className="gap-1 bg-blue-100">
                  <Landmark className="h-3 w-3" />
                  {instituicaoSelecionada}
                </Badge>
              </>
            )}
            {tipoOperacaoSelecionado && (
              <>
                <ChevronRight className="h-4 w-4 text-slate-400" />
                <Badge variant="secondary" className="gap-1 bg-orange-100">
                  <FileText className="h-3 w-3" />
                  {tipoOperacaoSelecionado}
                </Badge>
              </>
            )}
            {correspondenteSelecionado && (
              <>
                <ChevronRight className="h-4 w-4 text-slate-400" />
                <Badge variant="secondary" className="gap-1 bg-purple-100">
                  <Building2 className="h-3 w-3" />
                  {correspondenteSelecionado}
                </Badge>
              </>
            )}
            <Button 
              variant="ghost" 
              size="sm" 
              className="ml-2 text-xs text-red-600"
              onClick={onLimparFiltros}
            >
              Limpar filtros
            </Button>
          </div>
        )}
        
        {/* Botões de Gerenciamento */}
        <div className="mt-4 flex gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            className="gap-1 text-orange-700 border-orange-300 hover:bg-orange-50"
            onClick={onAbrirGerenciarTipos}
            data-testid="btn-gerenciar-tipos"
          >
            <Edit className="h-4 w-4" />
            Gerenciar Tipos de Operação
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-1 text-purple-700 border-purple-300 hover:bg-purple-50"
            onClick={onAbrirGerenciarCorrespondentes}
            data-testid="btn-gerenciar-correspondentes"
          >
            <Edit className="h-4 w-4" />
            Gerenciar Correspondentes
          </Button>
          <Button 
            size="sm"
            className="gap-1 bg-emerald-600 hover:bg-emerald-700"
            onClick={onAbrirNovaTabela}
            data-testid="btn-nova-tabela"
          >
            <Plus className="h-4 w-4" />
            Nova Tabela
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
